import {
  type EnrollmentAccountNameV2SubmitInput,
  useGetEnrollmentAccountNameV2Data,
  useSubmitEnrollmentAccountNameV2Data,
} from '@egw/api'
import { Button, Field, GenericError, Input, ModalDrawer, RadioGroup } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { EnrollmentV2Layout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { type FC, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'
import { PageHeader } from '../../../../components'
import { ROUTES } from '../../../../constants'
import { AccountTypeEnum } from '../../../../constants/api/account-type-enum'
import { useValidateRoute } from '../../../../hooks/navigation/validate-route'
import { isDateInPast, formatDateForAPI, getNameValidationRule } from '../../../../utils/form-validation'

const log = new Logger('Enrollment - AccountName')

export const AccountNameV2Page: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const { accountType } = useParams<{ accountType: string }>()

  const isInheritedIRA =
    accountType === AccountTypeEnum.TRADITIONAL_INHERITED || accountType === AccountTypeEnum.ROTH_INHERITED

  const {
    flags: { rolloutInheritedIraEnrollment },
  } = useFlags<FeatureFlags>()

  const { data, isLoading, error } = useGetEnrollmentAccountNameV2Data()

  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentAccountNameV2Data()

  // Default Account Name from API
  const defaultAccountName = data?.enrollmentData.recommendation?.investment?.account?.accountNickname
  const [updatedAccountName, setUpdatedAccountName] = useState<string | undefined>(defaultAccountName)

  // Modal
  const [showModal, setShowModal] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors: formErrors, touchedFields, isValid: formIsValid },
    trigger,
  } = useForm({
    defaultValues: {
      accountName: defaultAccountName,
      // Inherited IRA fields - using number values like Suitability Page
      inheritedFromSpouse: undefined as number | undefined,
      inheritedFromFirstName: '',
      inheritedFromLastName: '',
      iraBenefactorDeceasedDate: '',
    },
    mode: 'onChange',
  })

  useEffect(() => {
    void trigger()
  }, [trigger])

  useEffect(() => {
    if (data) {
      setUpdatedAccountName(defaultAccountName)
      reset({
        accountName: defaultAccountName,
        // Reset inherited IRA fields - using number values like Suitability Page
        inheritedFromSpouse: undefined,
        inheritedFromFirstName: '',
        inheritedFromLastName: '',
        iraBenefactorDeceasedDate: '',
      })
    }
  }, [data, reset, defaultAccountName])

  // Redirect if BE provides different route for the application
  useValidateRoute(data?.path)

  const onModalFormSubmit = (): void => {
    // On modal form submit, if value exists then save it under updatedAccountName
    const formData = getValues()
    if (formData.accountName) {
      setUpdatedAccountName(formData.accountName)
    }
    // Close modal
    setShowModal(false)
  }

  const onSubmit = handleSubmit(async (): Promise<void> => {
    if (defaultAccountName ?? updatedAccountName) {
      try {
        const formData = getValues()

        // Prepare the submission data
        const submitData: EnrollmentAccountNameV2SubmitInput = {
          investmentProfile: {
            account: {
              accountNickname: updatedAccountName ?? '',
            },
          },
        }

        // Add inherited IRA details if feature flag is enabled and fields are filled
        if (rolloutInheritedIraEnrollment && isInheritedIRA && submitData.investmentProfile) {
          if (formData.inheritedFromSpouse !== undefined) {
            submitData.investmentProfile.account.spouseInheritedIra = formData.inheritedFromSpouse === 1
          }

          if (formData.inheritedFromFirstName && formData.inheritedFromLastName) {
            submitData.investmentProfile.account.iraBenefactorName = {
              firstName: formData.inheritedFromFirstName,
              lastName: formData.inheritedFromLastName,
            }
          }

          // Add deceased date if provided, converting from MM/DD/YYYY to YYYY-MM-DD
          if (formData.iraBenefactorDeceasedDate) {
            submitData.investmentProfile.account.iraBenefactorDeceasedDate = formatDateForAPI(
              formData.iraBenefactorDeceasedDate,
            )
          }
        }
        const response = await mutate(submitData)
        if (response?.path) {
          navigate(response.path)
        } else {
          // TODO: Handle the case when path is not returned
          navigate(ROUTES.HOME.path)
        }
      } catch (e) {
        log.error('Error changing account name', e)
      }
    }
  })

  const closeHandler = (): void => {
    try {
      void mutate({
        abandonApplication: true,
      })
      navigate(WEALTH_ROUTES.INVESTMENTS.path)
    } catch (e) {
      log.error('Error abandoning application', e)
    }
  }

  const title = useMemo(() => {
    return rolloutInheritedIraEnrollment && isInheritedIRA
      ? t('investments_v2.account-name.title_titling')
      : t('investments_v2.account-name.title_non_inherited')
  }, [rolloutInheritedIraEnrollment, isInheritedIRA, t])

  return (
    <EnrollmentV2Layout closeHandler={closeHandler}>
      <div className="h-full flex flex-col w-full">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <form className="flex-1 flex flex-col justify-start">
            {/* Modal */}
            <ModalDrawer
              onClose={() => {
                setShowModal(false)
                if (!watch('accountName')) {
                  // Reset the form to the initial value if the modal is closed without submitting
                  reset({ accountName: updatedAccountName })
                }
              }}
              title={t(`investments_v2.account-name.modal_title`)}
              isOpen={showModal}
            >
              <div>
                <p className="typo-primary-sm-regular text-text-body mb-sm mt-xs">
                  {t('investments_v2.account-name.new_account_label')}
                </p>
                <Controller
                  name="accountName"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('investments_v2.account-name.account_name_label')}
                      required
                      error={formErrors.accountName?.message}
                      type="text"
                      maxLength={35}
                      className="text-md"
                    />
                  )}
                />
                <Button
                  type="button"
                  className="w-full mt-lg mb-lg"
                  testId="change-account-name-modal-button"
                  isDisabled={!watch('accountName') || updatedAccountName === watch('accountName')}
                  onClick={onModalFormSubmit}
                >
                  {t('common.update')}
                </Button>
              </div>
            </ModalDrawer>

            {/* Main page content */}
            <PageHeader title={title} />
            <p className="mb-xl typo-primary-sm-regular text-text-meta">
              {t('investments_v2.account-name.description')}
            </p>

            {/* Current account name */}
            <p className="typo-primary-base-semibold text-text-header mb-xs">
              {t('investments_v2.account-name.account_name_label')}
            </p>
            <Field
              className="border-b border-light pb-xs"
              valueClassName="typo-primary-base-light"
              value={updatedAccountName ?? ''}
              onEdit={() => {
                setShowModal(true)
              }}
              testId="account-name-field"
            />

            {/* Inherited IRA Fields - gated by feature flag */}
            {rolloutInheritedIraEnrollment && isInheritedIRA ? (
              <div className="mt-xl">
                {/* Spouse Status Section */}
                <div className="mb-lg">
                  <p className="text-sm font-semibold leading-md mb-xs text-text-header">
                    {t('investments_v2.account-name.inherited_ira.spouse_question')}
                  </p>
                  <Controller
                    name="inheritedFromSpouse"
                    control={control}
                    rules={{
                      required: t('common.required_field'),
                      validate: (value: unknown) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
                    }}
                    render={({ field: { ref: _ref, ...restField } }) => (
                      <RadioGroup
                        {...restField}
                        value={restField.value}
                        options={[
                          { value: 0, label: t('common.no') },
                          { value: 1, label: t('common.yes') },
                        ]}
                        name="inheritedFromSpouse"
                        testId="inherited-from-spouse_radio"
                      />
                    )}
                  />
                  {formErrors.inheritedFromSpouse ? (
                    <p className="text-sm text-red-600 mt-xs">{formErrors.inheritedFromSpouse.message}</p>
                  ) : null}
                </div>

                {/* Inherited From Name Section */}
                <div className="mb-lg">
                  <p className="text-sm font-semibold leading-md mb-xs text-text-header">
                    {t('investments_v2.account-name.inherited_ira.name_question')}
                  </p>
                  <div className="flex flex-row gap-4">
                    <div className="flex-1">
                      <Controller
                        name="inheritedFromFirstName"
                        control={control}
                        rules={{
                          required: t('common.required_field'),
                          ...getNameValidationRule(t('common.form_errors.inherited_first_name')),
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            label={t('investments_v2.account-name.inherited_ira.first_name_label')}
                            required
                            error={
                              touchedFields.inheritedFromFirstName
                                ? formErrors.inheritedFromFirstName?.message
                                : undefined
                            }
                            type="text"
                            isPII
                          />
                        )}
                      />
                    </div>
                    <div className="flex-1">
                      <Controller
                        name="inheritedFromLastName"
                        control={control}
                        rules={{
                          required: t('common.required_field'),
                          ...getNameValidationRule(t('common.form_errors.inherited_last_name')),
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            label={t('investments_v2.account-name.inherited_ira.last_name_label')}
                            required
                            error={
                              touchedFields.inheritedFromLastName
                                ? formErrors.inheritedFromLastName?.message
                                : undefined
                            }
                            type="text"
                            isPII
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Inherited IRA Deceased Date Section */}
                <div className="mb-lg">
                  <p className="text-sm font-semibold leading-md mb-xs text-text-header">
                    {t('investments_v2.account-name.inherited_ira.deceased_date_question')}
                  </p>
                  <Controller
                    name="iraBenefactorDeceasedDate"
                    control={control}
                    rules={{
                      required: t('common.required_field'),
                      validate: (value) => {
                        if (!value) return true // Let required rule handle empty values

                        // Check if date is complete (MM/DD/YYYY format)
                        if (value.length !== 10) {
                          return t('investments_v2.account-name.inherited_ira.deceased_date_error')
                        }

                        // Check if year is 4 digits
                        const [, , year] = value.split('/')
                        if (!year || year.length !== 4) {
                          return t('investments_v2.account-name.inherited_ira.deceased_date_error')
                        }

                        return isDateInPast(value) || t('investments_v2.account-name.inherited_ira.deceased_date_error')
                      },
                    }}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Input
                        {...field}
                        value={value}
                        onChange={(e) => {
                          const input = e.target.value.replace(/\D/g, '')
                          let formatted = ''

                          if (input.length > 0) {
                            formatted = input.slice(0, 2)
                            if (input.length > 2) {
                              formatted += `/${input.slice(2, 4)}`
                              if (input.length > 4) {
                                formatted += `/${input.slice(4, 8)}`
                              }
                            }
                          }

                          if (formatted.length <= 10) {
                            onChange(formatted)
                          }
                        }}
                        label={t('investments_v2.account-name.inherited_ira.deceased_date_label')}
                        required
                        error={
                          touchedFields.iraBenefactorDeceasedDate
                            ? formErrors.iraBenefactorDeceasedDate?.message
                            : undefined
                        }
                        type="text"
                        className="mb-lg"
                        isPII
                      />
                    )}
                  />
                </div>
              </div>
            ) : null}

            <div className="flex flex-col md:flex-row justify-between md:justify-start gap-3 mt-xl flex-1 md:flex-none">
              <Button
                type="button"
                className="w-full md:w-min"
                isDisabled={!watch('accountName') || !formIsValid}
                isLoading={isSubmitting}
                testId="enrollment-continue-button"
                onClick={() => void onSubmit()}
              >
                {t('common.continue')}
              </Button>
            </div>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {(error ?? submitError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentV2Layout>
  )
}
